import type { SrPartialPackingTypeMap } from '@plato/data/starrocks/types';
import { isUnbreakablePackageType } from '@plato/data/starrocks/types';
import { Minus, Plus } from 'lucide-react';
import { checkQuantityForRounding } from '../../features/catalog/utils/catalog-quantity-utils.ts';
import { useLocale } from '../../lib/hooks/use-locale.ts';
import { cn } from '../../lib/utils.ts';
import { Button } from '../ui/button.tsx';
import { InputNumber } from '../ui/input-number.tsx';

type Props = {
    className?: string;
    disabled?: boolean;
    onValueChange: (value: number) => void;
    value: number | null;
    quantityUnit: string;
    salesUnitsPerPackageUnit?: number;
    partialPackingType?: SrPartialPackingTypeMap;
};

export const IncrementalQuantityField: React.FC<Props> = ({
    className,
    disabled = false,
    quantityUnit,
    value: quantity,
    onValueChange,
    salesUnitsPerPackageUnit: propSalesUnitsPerPackageUnit,
    partialPackingType = 'UNKNOWN',
}) => {
    const { decimalSeparator, thousandSeparator } = useLocale();
    const hasToBeMultipleOfX = isUnbreakablePackageType(partialPackingType);
    const salesUnitsPerPackageUnit = propSalesUnitsPerPackageUnit || 1;

    const isCurrentValueAcceptable = (value: number) => {
        return hasToBeMultipleOfX && propSalesUnitsPerPackageUnit
            ? value % salesUnitsPerPackageUnit === 0
            : true;
    };

    const canDecreaseByOnePackage =
        typeof quantity === 'number' ? quantity - salesUnitsPerPackageUnit > 0 : quantity;

    const decreaseByOnePackage = () => {
        if (typeof quantity !== 'number') {
            return;
        }
        const roundedQuantity = Math.ceil(quantity / salesUnitsPerPackageUnit) * salesUnitsPerPackageUnit;
        onValueChange(Math.max(roundedQuantity - salesUnitsPerPackageUnit, 0));
    };

    const increaseByOnePackage = () => {
        if (typeof quantity !== 'number') {
            return;
        }
        const roundedQuantity = Math.floor(quantity / salesUnitsPerPackageUnit) * salesUnitsPerPackageUnit;
        onValueChange(roundedQuantity + salesUnitsPerPackageUnit);
    };

    return (
        <div className={cn('flex flex-row rounded-lg border border-neutral-100 w-full', className)}>
            <Button
                variant="subtle"
                className="aspect-square rounded-r-none p-0"
                disabled={!canDecreaseByOnePackage || disabled}
                onClick={decreaseByOnePackage}
            >
                <Minus className="h-4 w-4" />
            </Button>
            <div className="w-full">
                <InputNumber
                    errorBorder={typeof quantity === 'number' && !isCurrentValueAcceptable(quantity)}
                    value={quantity ?? ''}
                    onValueChange={({ value }) => {
                        // MARK - when this input is emptied it is equal to '' and not undefined
                        if (Number.isNaN(value) || value === '') {
                            onValueChange(0);
                        } else {
                            onValueChange(Number(value));
                        }
                    }}
                    suffix={` ${quantityUnit}`}
                    isAllowed={(values) => {
                        const { floatValue } = values;
                        return floatValue !== undefined && floatValue > 0;
                    }}
                    decimalSeparator={decimalSeparator}
                    thousandSeparator={thousandSeparator}
                    disabled={disabled}
                    className="rounded-none border-transparent focus:ring-1 focus:ring-neutral-400"
                    onKeyDown={(e) => {
                        if (e.keyCode === 13) {
                            (e.target as HTMLInputElement).blur();
                        }
                    }}
                    onBlur={() => {
                        if (!hasToBeMultipleOfX) {
                            return;
                        }

                        const checkedQuantity =
                            checkQuantityForRounding(
                                quantity,
                                salesUnitsPerPackageUnit,
                                partialPackingType,
                            ) ?? 0;

                        if (checkedQuantity !== quantity) {
                            onValueChange(checkedQuantity);
                        }
                    }}
                />
            </div>
            <Button
                variant="subtle"
                className="aspect-square  rounded-l-none p-0"
                onClick={increaseByOnePackage}
                disabled={disabled}
            >
                <Plus className="h-4 w-4" />
            </Button>
        </div>
    );
};
